import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Briefcase } from "lucide-react";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { JobActions } from "@/components/jobs/job-actions";
import { useDict, useLocale, fill } from "@/lib/i18n/context";
import type { Job } from "@/lib/types";

type JobWithCounts = Job & {
  candidate_count: string | number;
  completed_count: string | number;
};

export function JobsPage() {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const [jobs, setJobs] = useState<JobWithCounts[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch("/api/jobs", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<{ jobs: JobWithCounts[] }>;
      })
      .then((data) => {
        if (active) setJobs(data.jobs);
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (error || !jobs) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t.jobs.title}</h1>
          <p className="text-muted">{t.jobs.subtitle}</p>
        </div>
        <Link to="/jobs/new">
          <Button>
            <Plus className="h-4 w-4" /> {t.jobs.newJob}
          </Button>
        </Link>
      </div>

      {jobs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <Briefcase className="h-10 w-10 text-muted/60" />
            <p className="mt-4 font-medium">{t.jobs.empty}</p>
            <p className="text-sm text-muted">{t.jobs.emptyDesc}</p>
            <Link to="/jobs/new">
              <Button className="mt-4">
                <Plus className="h-4 w-4" /> {t.jobs.newJob}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs font-medium uppercase tracking-wide text-muted">
                  <th className="px-4 py-3 text-start">{t.jobs.colTitle}</th>
                  <th className="px-4 py-3 text-start">{t.jobs.colCandidates}</th>
                  <th className="hidden px-4 py-3 text-start sm:table-cell">
                    {t.jobs.colCompleted}
                  </th>
                  <th className="hidden px-4 py-3 text-start md:table-cell">
                    {t.jobs.colCreated}
                  </th>
                  <th className="px-4 py-3 text-end">{t.jobs.colActions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="font-medium hover:text-brand-600"
                      >
                        {job.title}
                      </Link>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Badge tone="green">{t.jobs.active}</Badge>
                        <Badge
                          tone={job.visibility === "public" ? "blue" : "neutral"}
                        >
                          {job.visibility === "public"
                            ? t.jobs.badgePublic
                            : t.jobs.badgePrivate}
                        </Badge>
                        {job.expires_at && (
                          <Badge
                            tone={
                              new Date(job.expires_at).getTime() < Date.now()
                                ? "red"
                                : "amber"
                            }
                          >
                            {new Date(job.expires_at).getTime() < Date.now()
                              ? t.jobs.listingExpired
                              : fill(t.jobs.listingExpires, {
                                  date: format(new Date(job.expires_at), "PP", {
                                    locale: dateLocale,
                                  }),
                                })}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/candidates?job=${job.id}`}
                        className="font-medium text-brand-600 hover:underline"
                      >
                        {Number(job.candidate_count)}
                      </Link>
                    </td>
                    <td className="hidden px-4 py-3 text-muted sm:table-cell">
                      {Number(job.completed_count)}
                    </td>
                    <td className="hidden px-4 py-3 text-xs text-muted md:table-cell">
                      {format(new Date(job.created_at), "PP", { locale: dateLocale })}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <JobActions jobId={job.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
